import { TokenName } from "../vars/system"
import { isDesktop, DesktopAuth } from "../desktop"

class AuthStorageAdapter {
  private inMemoryToken: string | null = null
  private isInitialized = false

  getInMemoryTokenSync(): string | null {
    if (this.inMemoryToken) {
      return this.inMemoryToken
    }
    if (typeof window !== "undefined") {
      try {
        const token =
          sessionStorage.getItem(TokenName) || localStorage.getItem(TokenName)
        if (token) {
          this.inMemoryToken = token
          return token
        }
      } catch {
        // Ignore storage access errors in restricted contexts
      }
    }
    return null
  }

  async getToken(): Promise<string | null> {
    if (this.inMemoryToken) {
      return this.inMemoryToken
    }

    // Desktop safeStorage / Keyring via DesktopAuth
    if (isDesktop()) {
      try {
        const token = await DesktopAuth.getToken()
        this.inMemoryToken = token
        this.isInitialized = true
        return token
      } catch (err) {
        console.error(
          "[AuthStorageAdapter]: Failed to get token from DesktopAuth:",
          err
        )
      }
    }

    // Web Browser Fallback (sessionStorage / localStorage)
    if (typeof window !== "undefined") {
      try {
        const token =
          sessionStorage.getItem(TokenName) || localStorage.getItem(TokenName)
        this.inMemoryToken = token
        this.isInitialized = true
        return token
      } catch {
        // Ignore
      }
    }

    this.isInitialized = true
    return null
  }

  async saveToken(token: string): Promise<boolean> {
    if (!token || typeof token !== "string" || token.trim() === "") {
      return false
    }
    this.inMemoryToken = token

    if (isDesktop()) {
      try {
        await DesktopAuth.saveToken(token)
      } catch (err) {
        console.error(
          "[AuthStorageAdapter]: Failed to save token via DesktopAuth:",
          err
        )
      }
    }

    if (typeof window !== "undefined") {
      try {
        sessionStorage.setItem(TokenName, token)
        localStorage.setItem(TokenName, token)
        return true
      } catch {
        return false
      }
    }

    return true
  }

  async clearToken(): Promise<boolean> {
    this.inMemoryToken = null

    let desktopSuccess = true
    if (isDesktop()) {
      try {
        desktopSuccess = await DesktopAuth.clearToken()
      } catch (err) {
        console.error(
          "[AuthStorageAdapter]: Failed to clear token via DesktopAuth:",
          err
        )
        desktopSuccess = false
      }
    }

    if (typeof window !== "undefined") {
      try {
        sessionStorage.removeItem(TokenName)
        localStorage.removeItem(TokenName)
      } catch {
        // Ignore
      }
    }

    return desktopSuccess
  }
}

export const authStorageAdapter = new AuthStorageAdapter()
